// Memories — a signal-backed scope dim over the backend `memory` signal_tags.
//
// A memory is the durable annotation the backend emits per memory-file
// read/write/edit: namespace="memory", tag=<slug>, value=JSON{op,body,refs,
// project}. The dim is reached PROMPT → SignalTags exactly like `signal`/`skill`,
// TAG-valued within the FIXED `memory` namespace (DIM_INFO.memory carries
// `viaSignalTags` + `signalNamespace:"memory"`): the dim value is the memory slug
// (st.tag), and the namespace gate keeps it to memory rows.
//
// FLAT (no project→file hierarchy): the tag is the bare slug, so the sidebar
// lists one row per slug. Same-slug files across projects/people MERGE — in
// practice only the reserved `MEMORY` index (shared by every endpoint), which is
// folded OUT of the listing here; descriptive leaf slugs are naturally
// 1-endpoint. Per-project attribution rides the `memory.project` child facet
// (memoryProjectFacet) — best-effort, since the planner can't GROUP BY the JSON
// `project` inside the value.
//
// EVERYTHING here reads the signal — no PROMPT_RESOURCE_PATHS path-scan and no
// per-prompt hydrate + Pyodide kernel anywhere in the memory surfaces. Besides the
// dim (listEntries + memoryCombinePredicates), this owns the give-it-back suite
// (listAllMemoryFiles / listMemoryPeople / listOrgLessons), the fleet matrix
// (memoryEndpointMatrix), and the body reader (listMemoryBodies) that the detail
// lens / store graph / X-ray all consume — the signal value already carries the
// body/op/refs, so nothing re-extracts client-side.
//
// Exports `memoryAdapter` (DimAdapter) for registration in dimAdapters.js; the
// dispatch sites in analyticsClient/apiClient handle the `memory` viaSignalTags
// join generically (no memory-specific branch beyond the namespace gate).
import {
    buildPromptRootAndFilter,
    rangeToTimeRange,
    rowsToObjects,
    runQuery as runAnalyticsQuery,
    bucketSecondsForRange,
    rangeBoundsMs,
    densifyBuckets,
} from "./analyticsClient.js";
import { shapeDimensionEntries } from "./dimAdapters.js";
import { compareDimEntries } from "./dimSort.js";
import { toApiRange } from "./shell.js";
// Pure extractor / label / index-parse helpers live in their own dependency-
// free module so parseApiRequest.js (and node tests) can use them without
// loading this analytics-heavy module. Re-exported here so existing importers
// are unchanged.
import {
    isMemoryIndexSlug,
    labelForMemory,
    memoryKindFromSlug,
    memoryProjectLabel,
} from "./memoryNames.js";
export { labelForMemory };

// ── Signal-backed dim (sidebar listing + scope predicate) ───────────
const NS = "memory";
const NS_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" };
const TAG_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" };
const ST_EVENT_ID = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID" };
const ST_VALUE = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_VALUE" };
const PROMPT_TS = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" };
const PROMPT_ENDPOINT = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" };
const SPARK_BUCKETS = 24;
// Exclude the reserved index tag (MEMORY) from grouped counts so the give-it-back
// roster/lessons count real memories, not the store index (matches the listing's
// isMemoryIndexSlug fold-out; MEMORY is the fixed MEMORY_INDEX_SLUG).
const NOT_INDEX_PRED = { pred: { field: TAG_FIELD, op: "OPERATOR_NE", value: "MEMORY" } };

// Breakdown / scope predicate: prompts whose signal_tags carry a `memory` tag in
// `ids`. Targets the joined `st` step (namespace gate + tag match), so the join
// must be present whenever it runs — predicateExtraDims:["memory"] forces it (the
// flow/contrast path). The passed stepAlias is intentionally ignored: the
// predicate lives on the join, not the PROMPT root. Mirrors skillCombinePredicates.
export function memoryCombinePredicates(ids = []) {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return null;
    return {
        andExpr: {
            children: [
                { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } },
                { pred: { field: TAG_FIELD, op: list.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ", value: list.length > 1 ? list : list[0] } },
            ],
        },
    };
}

// ── Facet ────────────────────────────────────────────────────────────
// Distinct memories + firing-prompt counts + inline sparklines, scoped + ranged,
// in one query. `extraDims:["memory"]` forces the SignalTags join; the namespace
// gate bounds the GROUP BY to memory rows; grouping by (tag, time bucket) gives a
// per-memory total plus a densified sparkline. COUNT_DISTINCT(event_id) — NOT
// COUNT — because one prompt emits several rows per file (a read+edit of the
// index averages ~3), so a plain COUNT runs ~2x hot. The reserved `MEMORY` index
// is folded OUT of the listing: it's the one slug shared across every endpoint,
// so flat would collapse many people's indexes into a single misleading row; it
// stays reachable via the per-memory Detail lens.
async function listEntries({ apiScope = [], offset = 0, limit = 100, search = "", sort = "activity", range, signalFilter = null, steer = null } = {}) {
    const apiRange = toApiRange(range);
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["memory"], steer });
    if (unsupported.length) return shapeDimensionEntries({ entries: [], total: 0 });
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } };
    // `signalFilter` (event_id IN a security signal's fired prompts) is layered in
    // when a signal is in scope, so the memory listing counts only signal-firing
    // prompts — the signal has no server predicate of its own (apiClient drops it
    // from apiScope and passes it here instead).
    const filterParts = [scopeFilter, nsGate, signalFilter].filter(Boolean);
    const bucketSeconds = bucketSecondsForRange(apiRange, SPARK_BUCKETS);
    const { fromMs, toMs } = rangeBoundsMs(apiRange);
    const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: filterParts.length === 1 ? filterParts[0] : { andExpr: { children: filterParts } },
        output: {
            dimensions: [
                { field: TAG_FIELD, outputAlias: "tag" },
                { field: PROMPT_TS, outputAlias: "bucket", bucketSeconds },
            ],
            // DISTINCT firing prompts per memory per bucket — the join fans a prompt
            // out to its tags, so a plain COUNT would over-count.
            metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
    };
    let rows = [];
    try {
        rows = rowsToObjects(await runAnalyticsQuery(spec));
    } catch (err) {
        console.warn("[memory] facet query failed:", err);
        return shapeDimensionEntries({ entries: [], total: 0 });
    }
    const seriesByTag = densifyBuckets(
        rows.filter((r) => !isMemoryIndexSlug(String(r.tag ?? "")))
            .map((r) => ({ id: String(r.tag ?? ""), bucket: r.bucket, value: r.value })),
        { fromMs, toMs, bucketCount: SPARK_BUCKETS, bucketSeconds },
    );
    const totalByTag = new Map();
    for (const r of rows) {
        const tag = String(r.tag ?? "");
        // Fold out the index — the one slug every endpoint shares; flat by slug
        // would merge many people's indexes into a single misleading row.
        if (!tag || isMemoryIndexSlug(tag)) continue;
        totalByTag.set(tag, (totalByTag.get(tag) || 0) + (Number(r.value) || 0));
    }
    const trimmed = String(search || "").trim().toLowerCase();
    let entries = [...totalByTag.keys()].map((tag) => ({
        id: tag,
        label: labelForMemory(tag),
        value: totalByTag.get(tag) || 0,
        sparkline: seriesByTag.get(tag) ?? new Array(SPARK_BUCKETS).fill(0),
    }));
    if (trimmed) entries = entries.filter((e) => e.label.toLowerCase().includes(trimmed) || e.id.toLowerCase().includes(trimmed));
    const sorted = entries.sort(compareDimEntries(sort));
    return shapeDimensionEntries({ entries: sorted.slice(offset, offset + limit), total: sorted.length });
}

// ── Give-it-back suite (signal-backed) ──────────────────────────────
// The Memory store / Across-endpoints views read the SAME `memory` signal_tags as
// the sidebar dim + detail lens, so their populations agree — no resource_paths
// superset that blanks the signal-backed detail on click, and counts match the
// sidebar. Endpoint spread comes from the prompt endpoint; per-key PROJECT spread
// rides the `memory.project` child facet (best-effort — the planner can't GROUP BY
// the project inside the memory JSON value). The reserved index tag is folded out
// via NOT_INDEX_PRED so counts are real memories.

// memory.project child facet → Map<key, Set<projectDir>>. `by` groups on the
// memory slug (tag) or the prompt endpoint. Forward-fill / non-load-bearing: a key
// with no memory.project rows yet resolves to no projects (0), never a blank.
export async function memoryProjectFacet({ apiScope = [], range, by = "tag", limit = 5000 } = {}) {
    const out = new Map();
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["memory"] });
    if (unsupported.length) return out;
    const keyField = by === "endpoint" ? PROMPT_ENDPOINT : TAG_FIELD;
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: "memory.project" } };
    const parts = [scopeFilter, nsGate].filter(Boolean);
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)),
        filter: parts.length === 1 ? parts[0] : { andExpr: { children: parts } },
        output: {
            dimensions: [
                { field: keyField, outputAlias: "key" },
                { field: ST_VALUE, outputAlias: "project" },
            ],
            metrics: [{ outputAlias: "n", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
        limit,
    };
    try {
        for (const r of rowsToObjects(await runAnalyticsQuery(spec))) {
            const key = String(r.key ?? "");
            const project = String(r.project ?? "");
            if (!key || !project) continue; // empty project = user-global memory (no spread)
            if (!out.has(key)) out.set(key, new Set());
            out.get(key).add(project);
        }
    } catch (err) {
        console.warn("[memory] project facet failed:", err);
    }
    return out;
}

// EVERY memory a person's harness tracks, flat — the "all memories" bridge so you
// don't drill project-by-project. Groups the `memory` signal by slug over the
// (endpoint-scoped) prompts; project dir rides the memory.project facet. Returns
// [{ projectDir, projectLabel, slug, label, kind, value }] sorted by activity.
export async function listAllMemoryFiles({ apiScope = [], range, limit = 500 } = {}) {
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["memory"] });
    if (unsupported.length) return [];
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } };
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)),
        filter: { andExpr: { children: [scopeFilter, nsGate, NOT_INDEX_PRED].filter(Boolean) } },
        output: {
            dimensions: [{ field: TAG_FIELD, outputAlias: "slug" }],
            metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
    };
    let rows = [];
    try {
        rows = rowsToObjects(await runAnalyticsQuery(spec));
    } catch (err) {
        console.warn("[memory] all-files query failed:", err);
        return [];
    }
    const projBySlug = await memoryProjectFacet({ apiScope, range, by: "tag" });
    const out = rows.map((r) => {
        const slug = String(r.slug ?? "");
        const projectDir = [...(projBySlug.get(slug) || [])][0] || "";
        return {
            projectDir,
            projectLabel: projectDir ? memoryProjectLabel(projectDir) : "",
            slug,
            label: labelForMemory(slug),
            kind: memoryKindFromSlug(slug),
            value: Number(r.value) || 0,
        };
    }).filter((r) => r.slug);
    out.sort((a, b) => (b.value || 0) - (a.value || 0));
    return out.slice(0, limit);
}

// PEOPLE (endpoints) that have memory + each one's distinct-memory count — the
// tenant roster for the "memory is per-person" model. Groups the `memory` signal by
// prompt endpoint (distinct slugs per endpoint, index folded out); project spread
// per endpoint rides the memory.project facet. Returns [{ endpointId, value,
// projectCount }] sorted by memory count. The caller resolves endpoint → person
// label and scopes to one to open their store.
export async function listMemoryPeople({ apiScope = [], range, limit = 100 } = {}) {
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["memory"] });
    if (unsupported.length) return [];
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } };
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)),
        filter: { andExpr: { children: [scopeFilter, nsGate, NOT_INDEX_PRED].filter(Boolean) } },
        output: {
            dimensions: [{ field: PROMPT_ENDPOINT, outputAlias: "endpoint" }],
            metrics: [{ outputAlias: "value", computed: { field: TAG_FIELD, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
    };
    let rows = [];
    try {
        rows = rowsToObjects(await runAnalyticsQuery(spec));
    } catch (err) {
        console.warn("[memory] people query failed:", err);
        return [];
    }
    const projByEp = await memoryProjectFacet({ apiScope, range, by: "endpoint" });
    return rows
        .map((r) => {
            const endpointId = r.endpoint != null ? String(r.endpoint) : "";
            return { endpointId, value: Number(r.value) || 0, projectCount: (projByEp.get(endpointId)?.size) || 0 };
        })
        .filter((p) => p.endpointId && p.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, limit);
}

// ORG LESSONS — the `memory` signal grouped by LESSON (slug) ACROSS endpoints, to
// surface SILOING (endpointCount 1 = a propagation opportunity; O-2345, the first
// give-it-back slice of the O-2344 thesis). Endpoint spread comes from the prompt
// endpoint, project spread from the memory.project facet; the index (MEMORY.md) is
// folded out — it's not a lesson. Counts ENDPOINTS, not people — each endpoint is
// its own agent context. Returns [{ slug, label, kind, endpointCount, projectCount,
// value }] sorted by activity.
export async function listOrgLessons({ apiScope = [], range, limit = 500 } = {}) {
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["memory"] });
    if (unsupported.length) return [];
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } };
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)),
        filter: { andExpr: { children: [scopeFilter, nsGate, NOT_INDEX_PRED].filter(Boolean) } },
        output: {
            dimensions: [{ field: TAG_FIELD, outputAlias: "slug" }],
            metrics: [
                { outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } },
                { outputAlias: "endpoints", computed: { field: PROMPT_ENDPOINT, op: "AGGREGATE_OP_COUNT_DISTINCT" } },
            ],
        },
    };
    let rows = [];
    try {
        rows = rowsToObjects(await runAnalyticsQuery(spec));
    } catch (err) {
        console.warn("[memory] org-lessons query failed:", err);
        return [];
    }
    const projBySlug = await memoryProjectFacet({ apiScope, range, by: "tag" });
    return rows
        .map((r) => {
            const slug = String(r.slug ?? "");
            return {
                slug,
                label: labelForMemory(slug),
                kind: memoryKindFromSlug(slug),
                endpointCount: Number(r.endpoints) || 0,
                projectCount: (projBySlug.get(slug)?.size) || 0,
                value: Number(r.value) || 0,
            };
        })
        .filter((l) => l.slug)
        .sort((a, b) => (b.value || 0) - (a.value || 0))
        .slice(0, limit);
}

// Read the `memory` signal bodies (value JSON: op / body / refs) newest-first —
// the durable replacement for the old listRecent* + hydratePrompt + Pyodide-kernel
// path (the signal already carries the body). `slug` scopes to one memory (the
// index "MEMORY", a cluster member, …); omit it for every memory in scope. The
// newest row per slug is the current copy; `op` distinguishes a full snapshot
// (write/read) from a partial `edit` hunk. Consumers: the store graph
// (useMemoryStore), the fleet-spread focal body, and the X-ray merge.
export async function listMemoryBodies({ apiScope = [], range, slug = null, limit = 500 } = {}) {
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["memory"] });
    if (unsupported.length) return [];
    const parts = [scopeFilter, { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } }];
    if (slug) parts.push({ pred: { field: TAG_FIELD, op: "OPERATOR_EQ", value: slug } });
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)),
        filter: { andExpr: { children: parts.filter(Boolean) } },
        output: {
            dimensions: [
                { field: TAG_FIELD, outputAlias: "slug" },
                { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, outputAlias: "event_id" },
                { field: PROMPT_TS, outputAlias: "event_time" },
                { field: ST_VALUE, outputAlias: "value" },
            ],
        },
        limit,
    };
    let rows = [];
    try {
        rows = rowsToObjects(await runAnalyticsQuery(spec));
    } catch (err) {
        console.warn("[memory] bodies query failed:", err);
        return [];
    }
    // Newest-first: event_time is a zone-less ISO string, so a lexical sort is
    // chronological (orderBy references an output alias, not a field — mirror
    // getSignalTagValues and sort here).
    rows.sort((a, b) => String(b.event_time ?? "").localeCompare(String(a.event_time ?? "")));
    return rows.map((r) => {
        let d = {};
        try { d = JSON.parse(r.value) ?? {}; } catch { /* malformed value → empty */ }
        return {
            eventId: r.event_id != null ? String(r.event_id) : "",
            ts: r.event_time ?? null,
            slug: String(r.slug ?? ""),
            op: d.op,
            body: typeof d.body === "string" ? d.body : "",
            refs: Array.isArray(d.refs) ? d.refs : [],
        };
    });
}

// Which endpoints carry which memory — a slug→Set(endpointId) matrix + the
// universe of memory-writing endpoints, from the `memory` signal grouped by
// (endpoint, slug) with the index folded out. One query. Powers the fleet-spread
// panel (has-it / missing) and X-ray cluster coverage.
export async function memoryEndpointMatrix({ apiScope = [], range } = {}) {
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["memory"] });
    const slugEndpoints = new Map();
    const allEndpoints = new Set();
    if (unsupported.length) return { slugEndpoints, allEndpoints };
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)),
        filter: { andExpr: { children: [scopeFilter, { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } }, NOT_INDEX_PRED].filter(Boolean) } },
        output: {
            dimensions: [
                { field: PROMPT_ENDPOINT, outputAlias: "endpoint" },
                { field: TAG_FIELD, outputAlias: "slug" },
            ],
            metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
    };
    // Let a query failure REJECT (don't swallow into an empty matrix): the view
    // caches only a resolved matrix, so empty-on-failure would hide coverage (0/0)
    // until reload. Rejecting lets the caller log it and retry on re-entry.
    const rows = rowsToObjects(await runAnalyticsQuery(spec));
    for (const r of rows) {
        const ep = r.endpoint != null ? String(r.endpoint) : null;
        const slug = String(r.slug ?? "");
        if (!ep || !slug) continue;
        allEndpoints.add(ep);
        if (!slugEndpoints.has(slug)) slugEndpoints.set(slug, new Set());
        slugEndpoints.get(slug).add(ep);
    }
    return { slugEndpoints, allEndpoints };
}

// Store neighbors of a memory — the OTHER slugs in the same store. Powers the
// fleet-spread "same store" view. Excludes the memory itself; dedups.
//
// Two sources, best-first:
//   • PROJECT (preferred, precise): a project's MEMORY.md IS the store, and the
//     decoded `projectDir` is the cross-device store key (same repo on two
//     machines = one store). Used when this memory has a known project. Blank
//     projects are pre-filtered by memoryProjectFacet, so this never degrades to
//     "match everything".
//   • ENDPOINT (fallback): when the memory has no project — either the
//     memory.project facet isn't populated yet (per-tenant / forward-fill) or it's
//     a user-global memory outside any project — the person's endpoint stands in
//     for the store (memory is per-endpoint).
// projectsBySlug is Map<slug, Set<projectDir>>; matrix is a memoryEndpointMatrix.
export function storeSiblingSlugs(slug, { matrix, projectsBySlug } = {}) {
    const mineProjects = projectsBySlug?.get?.(slug);
    if (mineProjects && mineProjects.size) {
        const out = [];
        for (const [otherSlug, projs] of projectsBySlug) {
            if (otherSlug === slug) continue;
            for (const p of projs) {
                if (mineProjects.has(p)) { out.push(otherSlug); break; }
            }
        }
        return out;
    }
    const slugEndpoints = matrix?.slugEndpoints;
    if (!slugEndpoints) return [];
    const mine = slugEndpoints.get(slug);
    if (!mine || !mine.size) return [];
    const out = [];
    for (const [otherSlug, eps] of slugEndpoints) {
        if (otherSlug === slug) continue;
        for (const e of eps) {
            if (mine.has(e)) { out.push(otherSlug); break; }
        }
    }
    return out;
}

async function count(opts = {}) {
    // Distinct memories reachable under scope — the facet gives us the set (index
    // folded out); its size is the count.
    const { entries = [] } = await listEntries({ ...opts, offset: 0, limit: 1000 });
    return entries.length;
}

// ── Per-tenant availability probe (gates the Memories dim) ──────────
// Does this tenant have ANY `memory` signal data? The dim is per-tenant (manifest
// deploy_to: none) with no resource_paths fallback, so the sidebar hides it until
// this resolves true — otherwise it renders an empty surface. Registered as the
// `getMemorySignalAvailable` query method (apiClient) so the sidebar reads it via
// useQuery (shared cache, reactive) rather than a bespoke store. Tenant-level
// (apiScope []) over a wide fixed window — NOT the UI range — so narrowing the time
// filter never hides a tenant that has memory data. Grouped by tag with limit 1: a
// metric-only COUNT always returns a row (n=0), so we group so an empty tenant
// yields zero rows.
const PROBE_RANGE = "1Q"; // 90-day presence window (signal_tags retention comfortably covers it)

export async function hasMemorySignal() {
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: [], extraDims: ["memory"] });
    if (unsupported.length) return false;
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } };
    const spec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(PROBE_RANGE)),
        filter: { andExpr: { children: [scopeFilter, nsGate].filter(Boolean) } },
        output: {
            dimensions: [{ field: TAG_FIELD, outputAlias: "tag" }],
            metrics: [{ outputAlias: "n", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
        limit: 1,
    };
    const rows = rowsToObjects(await runAnalyticsQuery(spec));
    return rows.length > 0;
}

// ── Adapter export ─────────────────────────────────────────────────

export const memoryAdapter = {
    id:     "memory",
    plural: "memories",
    label:  "Memories",
    description: "Memories tracked by agent harnesses in each prompt",

    predicate: memoryCombinePredicates,
    predicateExtraDims: ["memory"],
    // The predicate is "st.tag is one of ids" under the memory namespace, so
    // grouping by the tag gives every memory's flow links in one read.
    flowGroup: { field: TAG_FIELD, idsForValue: (value, ids) => ids.filter((id) => String(id) === String(value)) },
    listEntries,
    count,
    labelFor: labelForMemory,

    // Capability flags
    composesWithPromptGraph: true,
    bodyFitsInListProjection: true,
    countBatchable: false,
};
